import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const s = String(fecha).substring(0, 10);
    const [year, month, day] = s.split('-');
    if (!day) return s;
    return `${day}/${month}/${year}`;
};

const formatearNumero = (valor) => {
    if (valor == null) return '0';
    return Number(valor).toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const hoyISO = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const estadoTexto = (e) => e === 'C' ? 'Cerrada' : e === 'A' ? 'Anulada' : 'Abierta';

const claveF = (f) => `${f.idSucursal}|${String(f.tipoDoc).trim()}|${f.boca}|${f.nfactura}|${String(f.timbrado).trim()}`;
const claveP = (p) => `${p.id_sucursal}|${String(p.tipo_doc).trim()}|${p.boca}|${p.nfactura}|${String(p.timbrado).trim()}`;
const PREP_TXT = { P: 'Preparando', L: 'Listo', E: 'Entregado' };
const PREP_COLOR = { P: '#d97706', L: '#0d6efd', E: '#1b5e20' };

function EntregaFacturas({ usuario }) {
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());
    const [facturas, setFacturas] = useState([]);
    const [tiposEntrega, setTiposEntrega] = useState([]);
    const [prepMap, setPrepMap] = useState({});
    const [cargando, setCargando] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState(''); // '' todos, SIN, P, L, E
    const [autoRefresh, setAutoRefresh] = useState(true);

    const nombreUsuario = usuario?.idUsuario || usuario?.mail || usuario?.usuario || usuario?.nombre || '';

    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(20);

    useEffect(() => {
        axios.get(getApiUrl('/api/facturas/tipos-entrega'))
            .then(r => setTiposEntrega(r.data || []))
            .catch(e => console.error('Error tipos entrega:', e));
    }, []);

    useEffect(() => {
        buscar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mapa id_tipoent -> nom_entrega
    const nomEntrega = (idEnt) => {
        const id = String(idEnt || '').trim();
        if (!id) return '';
        const te = tiposEntrega.find(t => String(t.id_tipoent).trim() === id);
        return te ? te.nom_entrega : id;
    };

    const cargarPrep = async (d, h) => {
        try {
            const r = await axios.get(getApiUrl('/api/prep-factura/por-fecha'), { params: { desde: d, hasta: h } });
            const map = {};
            (r.data || []).forEach(p => { map[claveP(p)] = p; });
            setPrepMap(map);
        } catch (e) { console.error('Error prep:', e); setPrepMap({}); }
    };

    const buscar = async () => {
        if (!desde || !hasta) { alert('Ingresá el rango de fechas.'); return; }
        setCargando(true); setPaginaActual(1);
        try {
            const r = await axios.get(getApiUrl('/api/facturas/por-fecha'), { params: { desde, hasta } });
            setFacturas(r.data || []);
            await cargarPrep(desde, hasta);
        } catch (e) {
            console.error('Error facturas por fecha:', e);
            alert('Error al buscar: ' + (e.response?.data || e.message));
            setFacturas([]);
        } finally {
            setCargando(false);
        }
    };

    // Refresco silencioso (respeta el rango de fechas vigente; no resetea pagina ni filtros)
    const refrescar = async () => {
        if (!desde || !hasta) return;
        try {
            const r = await axios.get(getApiUrl('/api/facturas/por-fecha'), { params: { desde, hasta } });
            setFacturas(r.data || []);
            await cargarPrep(desde, hasta);
        } catch (e) { /* silencioso */ }
    };

    // Auto-actualizacion cada 10 segundos
    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(() => { refrescar(); }, 10000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefresh, desde, hasta]);

    const bodyFactura = (f) => ({
        idSucursal: f.idSucursal, tipoDoc: String(f.tipoDoc).trim(), boca: f.boca,
        nfactura: f.nfactura, timbrado: String(f.timbrado).trim(), usuario: nombreUsuario
    });

    const prepararEImprimir = async (f) => {
        try {
            await axios.post(getApiUrl('/api/prep-factura/preparar'), bodyFactura(f));
            const p = new URLSearchParams({ idSucursal: f.idSucursal, tipoDoc: String(f.tipoDoc).trim(), boca: f.boca, nfactura: f.nfactura, timbrado: String(f.timbrado).trim() });
            window.open(getApiUrl('/api/prep-factura/imprimir') + '?' + p.toString(), '_blank');
            await cargarPrep(desde, hasta);
        } catch (e) { alert('No se pudo preparar: ' + (e.response?.data || e.message)); }
    };

    const marcarListo = async (f) => {
        try {
            await axios.post(getApiUrl('/api/prep-factura/listo'), bodyFactura(f));
            await cargarPrep(desde, hasta);
        } catch (e) { alert('No se pudo marcar Listo: ' + (e.response?.data || e.message)); }
    };

    const marcarEntregar = async (f) => {
        if (!window.confirm(`Marcar como ENTREGADO la factura ${String(f.idSucursal).padStart(3,'0')}-${String(f.boca).padStart(3,'0')}-${f.nfactura}?\n\nSe descontará el stock de esta factura.`)) return;
        try {
            await axios.post(getApiUrl('/api/prep-factura/entregar'), bodyFactura(f));
            await cargarPrep(desde, hasta);
        } catch (e) { alert('No se pudo entregar: ' + (e.response?.data || e.message)); }
    };

    const filtradas = () => {
        const b = busqueda.trim().toLowerCase();
        return facturas.filter(f => {
            // Filtro por estado de preparacion
            if (filtroEstado) {
                const est = prepMap[claveF(f)]?.estado;
                if (filtroEstado === 'SIN') { if (est) return false; }
                else if (est !== filtroEstado) return false;
            }
            if (!b) return true;
            return String(f.nfactura || '').includes(b) ||
                String(f.timbrado || '').includes(b) ||
                String(f.nomCliente || '').toLowerCase().includes(b) ||
                String(f.rucCliente || '').toLowerCase().includes(b) ||
                nomEntrega(f.entrega).toLowerCase().includes(b);
        });
    };

    const lista = filtradas();
    const totalMonto = lista.reduce((s, f) => s + (Number(f.monTotal) || 0), 0);
    const totalSaldo = lista.reduce((s, f) => s + (Number(f.saldo) || 0), 0);

    const totalPaginas = Math.max(1, Math.ceil(lista.length / itemsPorPagina));
    const indicePrimero = (paginaActual - 1) * itemsPorPagina;
    const indiceUltimo = indicePrimero + itemsPorPagina;
    const actuales = lista.slice(indicePrimero, indiceUltimo);

    const exportarExcel = () => {
        const datos = lista.map(f => ({
            'Numero': `${String(f.idSucursal).padStart(3, '0')}-${String(f.boca).padStart(3, '0')}-${f.nfactura}`,
            'Timbrado': f.timbrado,
            'Tipo': f.tipoDoc,
            'Fecha': f.fecha ? formatearFecha(f.fecha) : '',
            'Cliente': f.nomCliente,
            'RUC': f.rucCliente,
            'Tipo Entrega': nomEntrega(f.entrega),
            'Total': Number(f.monTotal) || 0,
            'Saldo': Number(f.saldo) || 0,
            'Estado': estadoTexto(f.estado)
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Facturas');
        XLSX.writeFile(wb, 'facturas_emitidas.xlsx');
    };

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Entrega de Facturas</h2>
                <div className="acciones-header">
                    <input type="text" className="input-busqueda" placeholder="Filtrar resultados..."
                        value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPaginaActual(1); }} />
                    <button className="btn btn-exportar" onClick={exportarExcel} disabled={lista.length === 0}>Exportar Excel</button>
                </div>
            </div>

            {/* Filtros de fecha */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Desde</label>
                    <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="form-input" />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Hasta</label>
                    <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="form-input" />
                </div>
                <button className="btn btn-nuevo" onClick={buscar} disabled={cargando}>
                    {cargando ? 'Buscando...' : '🔍 Buscar'}
                </button>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Estado preparación</label>
                    <select value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setPaginaActual(1); }}
                        className="form-input" style={{ minWidth: '160px' }}>
                        <option value="">Todos</option>
                        <option value="SIN">Sin preparar</option>
                        <option value="P">Preparando</option>
                        <option value="L">Listo</option>
                        <option value="E">Entregado</option>
                    </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
                    <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                    Auto 10s
                </label>
                {facturas.length > 0 && <span style={{ fontSize: '13px', color: '#7f8c8d' }}>{lista.length} facturas</span>}
            </div>

            {/* Grilla */}
            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>Numero</th><th>Timbrado</th><th>Tipo</th><th>Fecha</th>
                            <th>Cliente</th><th>RUC</th><th>Tipo Entrega</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Saldo</th>
                            <th>Estado</th>
                            <th>Preparación</th>
                            <th>Acc</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargando ? <tr><td colSpan="12" className="sin-datos">Cargando...</td></tr>
                        : actuales.length === 0 ? <tr><td colSpan="12" className="sin-datos">No hay facturas en el rango</td></tr>
                        : actuales.map((f, i) => {
                            const prep = prepMap[claveF(f)];
                            const est = prep?.estado;
                            const anulada = f.estado === 'A';
                            return (
                            <tr key={i}>
                                <td>{String(f.idSucursal).padStart(3, '0')}-{String(f.boca).padStart(3, '0')}-{f.nfactura}</td>
                                <td>{f.timbrado}</td>
                                <td>{f.tipoDoc}</td>
                                <td>{f.fecha ? formatearFecha(f.fecha) : ''}</td>
                                <td>{f.nomCliente}</td>
                                <td>{f.rucCliente}</td>
                                <td>{nomEntrega(f.entrega)}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(f.monTotal)}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(f.saldo)}</td>
                                <td style={f.estado === 'A' ? { color: '#c62828', fontWeight: 'bold' } : {}}>{estadoTexto(f.estado)}</td>
                                <td style={{ color: est ? PREP_COLOR[est] : '#999', fontWeight: est ? 'bold' : 'normal' }}
                                    title={prep ? `Prep: ${prep.usu_prep || ''} ${prep.fch_prep ? formatearFecha(prep.fch_prep) : ''}\nListo: ${prep.usu_listo || ''} ${prep.fch_listo ? formatearFecha(prep.fch_listo) : ''}\nEntrega: ${prep.usu_entrega || ''} ${prep.fch_entrega ? formatearFecha(prep.fch_entrega) : ''}` : ''}>
                                    {est ? PREP_TXT[est] : '—'}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" onClick={() => prepararEImprimir(f)} className="btn-accion"
                                        title="Preparar e imprimir comprobante" disabled={anulada}
                                        style={anulada ? { opacity: 0.3 } : { color: '#6A1B9A' }}>📦</button>
                                    <button type="button" onClick={() => marcarListo(f)} className="btn-accion"
                                        title="Marcar como Listo" disabled={anulada || est !== 'P'}
                                        style={(anulada || est !== 'P') ? { opacity: 0.3 } : { color: '#0d6efd' }}>✅</button>
                                    <button type="button" onClick={() => marcarEntregar(f)} className="btn-accion"
                                        title="Marcar como Entregado (descuenta stock)" disabled={anulada || est !== 'L'}
                                        style={(anulada || est !== 'L') ? { opacity: 0.3 } : { color: '#1b5e20' }}>🚚</button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                    {lista.length > 0 && (
                        <tfoot>
                            <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                <td colSpan="7" style={{ textAlign: 'right' }}>Totales:</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(totalMonto)}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(totalSaldo)}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Paginacion */}
            {lista.length > 0 && (
                <div className="paginacion-container">
                    <div className="paginacion-info">
                        <span>Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, lista.length)} de {lista.length}</span>
                        <select value={itemsPorPagina} onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }} className="select-registros">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
                        </select>
                    </div>
                    <div className="paginacion-controles">
                        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} className="btn-paginacion">|&lt;</button>
                        <button onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1} className="btn-paginacion">&lt;</button>
                        <button onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;</button>
                        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;|</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EntregaFacturas;
